import React, { useState } from 'react';

const SAMPLE_HTML = `<!DOCTYPE html>
<html lang="en">
<head>
  <title>Classic Apple Pie Recipe — Bake Like Grandma</title>
  <meta name="description" content="A flaky, buttery apple pie with cinnamon-spiced filling. Ready in 90 minutes.">
  <meta property="og:image" content="https://example.com/apple-pie.jpg">
</head>
<body>
  <article>
    <h1>Classic Apple Pie</h1>
    <p>By <a rel="author" href="/author/jane">Jane Smith</a> · Published <time datetime="2025-09-12">September 12, 2025</time></p>
    <img src="/apple-pie.jpg" alt="A golden apple pie on a wooden table">
    <p>The flakiest, butteriest, most cinnamon-forward apple pie you'll ever bake.</p>

    <h2>Ingredients</h2>
    <ul>
      <li>6 Granny Smith apples, peeled and sliced</li>
      <li>3/4 cup sugar</li>
      <li>2 tsp cinnamon</li>
      <li>2 pie crusts</li>
      <li>2 tbsp butter</li>
    </ul>

    <h2>Instructions</h2>
    <ol>
      <li>Preheat oven to 425°F.</li>
      <li>Mix apples, sugar, and cinnamon in a large bowl.</li>
      <li>Roll out one crust and place in pie pan.</li>
      <li>Fill with apple mixture and dot with butter.</li>
      <li>Cover with second crust and crimp edges.</li>
      <li>Bake 45 minutes until crust is golden.</li>
    </ol>

    <p>Prep time: 20 minutes · Cook time: 45 minutes · Yield: 8 servings</p>
  </article>
</body>
</html>`;

interface AnalyzeFormProps {
  busy: boolean;
  busyLabel?: string;
  onSubmitHtml: (html: string) => void;
  onSubmitUrl: (url: string) => void;
}

type Mode = 'url' | 'html';

export const AnalyzeForm: React.FC<AnalyzeFormProps> = ({
  busy,
  busyLabel,
  onSubmitHtml,
  onSubmitUrl,
}) => {
  const [mode, setMode] = useState<Mode>('url');
  const [url, setUrl] = useState('');
  const [html, setHtml] = useState('');

  const submit = () => {
    if (busy) return;
    if (mode === 'url') {
      const trimmed = url.trim();
      if (!trimmed) return;
      onSubmitUrl(trimmed);
    } else {
      const trimmed = html.trim();
      if (!trimmed) return;
      onSubmitHtml(trimmed);
    }
  };

  return (
    <div className="card">
      <div className="tabs" role="tablist" aria-label="Input mode">
        <button
          type="button"
          role="tab"
          aria-selected={mode === 'url'}
          className={`tab ${mode === 'url' ? 'is-active' : ''}`}
          onClick={() => setMode('url')}
        >
          Analyze a URL
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={mode === 'html'}
          className={`tab ${mode === 'html' ? 'is-active' : ''}`}
          onClick={() => setMode('html')}
        >
          Paste HTML
        </button>
      </div>

      {mode === 'url' ? (
        <div>
          <label className="eyebrow" htmlFor="url-input" style={{ display: 'block', marginBottom: 8 }}>
            URL
          </label>
          <input
            id="url-input"
            type="url"
            className="input"
            placeholder="https://example.com/recipe-or-product-or-article"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') submit();
            }}
            autoComplete="off"
            spellCheck={false}
          />
          <p className="meta" style={{ marginTop: 8 }}>
            We render the page (JS executed) via Firecrawl, then detect which Schema.org types fit the content.
          </p>
        </div>
      ) : (
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 8 }}>
            <label className="eyebrow" htmlFor="html-input">
              Rendered HTML
            </label>
            <button
              type="button"
              className="btn btn-sm"
              style={{ background: 'transparent', color: 'var(--signal)', padding: '4px 0' }}
              onClick={() => setHtml(SAMPLE_HTML)}
            >
              Load recipe example
            </button>
          </div>
          <textarea
            id="html-input"
            className="textarea"
            placeholder="<!-- Paste rendered HTML — full document or just the <main> content -->"
            value={html}
            onChange={(e) => setHtml(e.target.value)}
            spellCheck={false}
          />
        </div>
      )}

      <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 20 }}>
        <button
          type="button"
          className="btn btn-primary"
          onClick={submit}
          disabled={busy || (mode === 'url' ? !url.trim() : !html.trim())}
        >
          {busy ? (
            <>
              <span className="spinner" />
              {busyLabel || 'Working…'}
            </>
          ) : (
            <>
              Detect schemas
              <svg width="14" height="14" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
              </svg>
            </>
          )}
        </button>
      </div>
    </div>
  );
};
